<template>
  <section class="card-category-page">
    <AppNavBar
      :title="t('card.title')"
      :left-arrow="false"
      background-color="#f5f5f5"
    >
      <template #right>
        <button
          type="button"
          class="card-category__contact"
          @click="router.push('/mine/about')"
        >
          <AppSvgIcon name="contack_icon" size="22" color="#000" />
        </button>
      </template>
    </AppNavBar>

    <div class="card-category__body">
      <div
        v-for="item in entranceItems"
        :key="item.key"
        class="card-category__item"
        @click="handleEntrance(item)"
      >
        <app-image class="card-category__cover" :src="item.image" alt="" />
        <div class="card-category__content">
          <div class="card-category__title">{{ item.title }}</div>
          <p class="card-category__desc">{{ item.desc }}</p>
        </div>
        <app-image class="card-category__arrow" :src="arrowIcon" alt="" />
      </div>
    </div>

    <van-popup
      v-model:show="showKycDialog"
      class="kyc-dialog-popup"
      :close-on-click-overlay="false"
    >
      <section class="kyc-dialog">
        <AppSvgIcon
          class="kyc-dialog__icon"
          color="#fff"
          name="kyc_dialog_icon"
          size="50"
        />
        <button type="button" class="kyc-dialog__close" @click="closeKycDialog">
          <app-image
            class="app-cancel-icon"
            src="@/assets/images/cancel_gray.png"
            alt=""
          />
        </button>
        <h2>{{ t("card.kycDialogTitle") }}</h2>
        <p>{{ t("card.kycDialogDesc") }}</p>
        <div class="kyc-dialog__actions">
          <button
            type="button"
            class="kyc-dialog__cancel"
            @click="closeKycDialog"
          >
            {{ t("common.cancel") }}
          </button>
          <button
            type="button"
            class="kyc-dialog__confirm"
            @click="confirmKycDialog"
          >
            {{ t("card.kycDialogAction") }}
          </button>
        </div>
      </section>
    </van-popup>
  </section>
</template>

<script setup>
import { computed, ref } from "vue";
import { closeToast, showFailToast, showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { pcCardCreatePre } from "@/api/card";
import { useProfileStore } from "@/stores/profile";
import { navigateWithState } from "@/utils/route-state";
import { useThemeStore } from "@/stores/theme";
import arrowIcon from "@/assets/images/cardImage/arrow_icon.png";
import img2 from "@/assets/images/SVG/img2.png";
import img3 from "@/assets/images/SVG/img3.png";
import img4 from "@/assets/images/SVG/img4.png";
import img5 from "@/assets/images/SVG/img5.png";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const themeStore = useThemeStore();
const showKycDialog = ref(false);

const entranceItems = computed(() => [
  {
    key: "virtual",
    type: 2,
    image: themeStore.image("img2", img2),
    title: t("card.applyCardTypeOne"),
    desc: t("card.applyCardTip1"),
  },
  {
    key: "physical",
    type: 1,
    image: themeStore.image("img3", img3),
    title: t("card.applyCardTypeTwo"),
    desc: t("card.applyCardTip2"),
  },
  {
    key: "activate",
    image: themeStore.image("img4", img4),
    title: t("wallet.activatePhysicalCard"),
    desc: t("card.applyCardTip3"),
  },
  {
    key: "myCards",
    image: themeStore.image("img5", img5),
    title: t("card.myCard"),
    desc: t("card.applyCardTip4"),
  },
]);

function goMyCards() {
  navigateWithState(router, "/card/index", {
    namespace: "home-card",
    payload: {
      leftArrow: true,
      backTo: "/home/card",
    },
  });
}

function goApprovedFeature(item, result = {}) {
  if (item.key === "activate") {
    navigateWithState(router, "/wallet/physical-active", {
      namespace: "wallet-physical-active",
      payload: {
        email: result?.email || profileStore.currentAccount?.email || "",
      },
    });
    return;
  }

  navigateWithState(router, "/wallet/open-card", {
    namespace: "card-open",
    payload: {
      type: String(item.type || 2),
      hideTab: true,
      forceBackHome: true,
    },
  });
}

function goKycResult(holderStatus, result = {}, item = {}) {
  navigateWithState(router, "/card/kyc/result", {
    namespace: "card-kyc-result",
    payload: {
      holderStatus: String(holderStatus || 1),
      reason: result?.reason || "",
      type: String(item.type || 1),
    },
  });
}

function closeKycDialog() {
  showKycDialog.value = false;
}

function confirmKycDialog() {
  closeKycDialog();
  router.push("/card/kyc");
}

async function handleEntrance(item) {
  if (item.key === "myCards") {
    goMyCards();
    return;
  }

  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    const result = await pcCardCreatePre({
      address: profileStore.platformAddress,
    });
    const holderStatus = Number(result?.holderStatus || 0);

    if (holderStatus === 3) {
      goApprovedFeature(item, result);
      return;
    }

    if (
      holderStatus === 2 ||
      holderStatus === 4 ||
      (holderStatus === 1 && String(result?.reason || "").trim())
    ) {
      goKycResult(holderStatus, result, item);
      return;
    }

    showKycDialog.value = true;
  } catch (error) {
    showFailToast(error.message || error.msg || t("common.retryLater"));
  } finally {
    closeToast();
  }
}
</script>

<style scoped lang="scss">
.card-category-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  overflow: hidden;
  padding: env(safe-area-inset-top) 0 0;
  background: #f5f5f5;
}

.card-category__contact {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
}

.card-category__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 30px 15px 96px;

  .card-category__item {
    display: flex;
    width: 100%;
    align-items: center;
    margin: 0 0 20px;
    padding: 20px 15px;
    border: 0;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.02);
    color: #000000;
    text-align: left;
  }
}

.card-category__cover {
  flex: 0 0 auto;
  width: 88px;
  height: 89px;

  :deep(.van-image__img) {
    object-fit: contain;
  }
}

.card-category__content {
  flex: 1;
  min-width: 0;
  margin: 0 14px;
}

.card-category__title {
  color: #000000;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
}

.card-category__desc {
  margin: 10px 0 0;
  color: #999999;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.6;
}

.card-category__arrow {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;

  :deep(.van-image__img) {
    object-fit: contain;
  }
}

:global(.kyc-dialog-popup) {
  overflow: visible;
  border-radius: 12px;
  background: transparent;
}

.kyc-dialog {
  position: relative;
  box-sizing: border-box;
  width: 300px;
  padding: 34px 16px 20px;
  border-radius: 12px;
  background: #ffffff;
  text-align: center;
}

.kyc-dialog__icon {
  position: absolute;
  top: -25px;
  left: 50%;
  width: 50px;
  height: 50px;
  transform: translateX(-50%);

  :deep(.van-image__img) {
    object-fit: contain;
  }
}

.kyc-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 18px;
    height: 18px;
  }
}

.kyc-dialog h2 {
  margin: 0;
  color: #090909;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
}

.kyc-dialog p {
  margin: 12px 0 16px;
  color: #444444;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
}

.kyc-dialog__actions {
  display: flex;
  gap: 10px;
}

.kyc-dialog__actions button {
  flex: 1;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 400;
}

.kyc-dialog__cancel {
  background: var(--wp-color-button-3);
  color: var(--wp-color-theme);
}

.kyc-dialog__confirm {
  background: var(--wp-color-theme);
  color: #ffffff;
}
</style>
